// Boss controller: gives boss enemies special periodic abilities (summons, etc.).
//
// Bosses are flagged via `EnemyStats.boss = true`. The controller scans the
// active enemies array every tick, and fires per-key abilities on a cooldown.
// It is intentionally simple — abilities are expressed as "spawn N enemies of
// type X every Y ms" so we don't need new combat primitives.

import Phaser from "phaser";
import { ENEMIES } from "../config";
import { Enemy } from "../entities/Enemy";

interface BossPattern {
  /** Cooldown between ability uses, in scaled ms. */
  cooldownMs: number;
  /** Enemy type to summon. */
  summon: keyof typeof ENEMIES;
  /** How many minions per ability use. */
  count: number;
}

const PATTERNS: Record<string, BossPattern> = {
  boss_warlord:       { cooldownMs: 8000,  summon: "orc",             count: 2 },
  boss_bridge_troll:  { cooldownMs: 6000,  summon: "swarmling",       count: 3 },
  boss_frost_wyrm:    { cooldownMs: 10000, summon: "wraith",          count: 4 },
  boss_rock_wyrm:     { cooldownMs: 8000,  summon: "kobold_rider",    count: 3 },
  boss_bog_giant:     { cooldownMs: 9000,  summon: "swarmling",       count: 5 },
  boss_sea_leviathan: { cooldownMs: 7000,  summon: "wraith",          count: 4 },
  boss_siege_golem:   { cooldownMs: 9000,  summon: "armored_orc",     count: 2 },
  boss_shadow_titan:  { cooldownMs: 7000,  summon: "shadow_assassin", count: 3 }
};

interface BossState { remainingMs: number; }

export class BossController {
  private states = new WeakMap<Enemy, BossState>();
  /** Currently tracked boss for the top-of-screen HP bar (most recent boss spawn). */
  private trackedBoss: Enemy | null = null;
  private listeners: ((e: Enemy | null) => void)[] = [];

  constructor(
    private scene: Phaser.Scene,
    private waypoints: { x: number; y: number }[],
    private addEnemy: (e: Enemy) => void
  ) {}

  onTrackedChanged(fn: (e: Enemy | null) => void): void { this.listeners.push(fn); }
  private emitTracked(): void { for (const l of this.listeners) l(this.trackedBoss); }

  get tracked(): Enemy | null { return this.trackedBoss; }

  update(_time: number, deltaMs: number, enemies: Enemy[]): void {
    let foundBoss: Enemy | null = null;
    for (const e of enemies) {
      if (!e.alive || !e.stats.boss) continue;
      foundBoss = e;
      let st = this.states.get(e);
      if (!st) {
        const pat = PATTERNS[e.stats.key];
        st = { remainingMs: pat?.cooldownMs ?? 8000 };
        this.states.set(e, st);
      }
      const pat = PATTERNS[e.stats.key];
      if (!pat) continue;
      st.remainingMs -= deltaMs;
      if (st.remainingMs <= 0) {
        st.remainingMs += pat.cooldownMs;
        this.summonNear(e, pat);
      }
    }
    if (foundBoss !== this.trackedBoss) {
      this.trackedBoss = foundBoss;
      this.emitTracked();
    }
  }

  private summonNear(boss: Enemy, pat: BossPattern): void {
    const stats = ENEMIES[pat.summon];
    if (!stats) return;
    // Spawn minions at the boss's current position; they march along the same
    // path starting from whichever waypoint the boss is currently heading to.
    for (let i = 0; i < pat.count; i++) {
      const minion = new Enemy(this.scene, stats, this.waypoints);
      // Insert at boss position so they don't teleport back to the spawn point.
      minion.x = boss.x + (Math.random() - 0.5) * 24;
      minion.y = boss.y + (Math.random() - 0.5) * 24;
      minion.waypointIdx = boss.waypointIdx;
      this.addEnemy(minion);
    }
  }
}
